import { ToolInputRowEditor } from './ToolInputRowEditor';
import {
  inputLocationForMethod,
  simplePathPlaceholderNames,
  type InputRowsIssue,
  type ToolInputRow,
} from './tool-inputs';
import styles from './ToolForm.module.css';

interface ToolInputsTableProps {
  rows: ToolInputRow[];
  issues: InputRowsIssue[];
  method: string;
  pathTemplate: string;
  onChange: (rows: ToolInputRow[]) => void;
}

export const ToolInputsTable = ({
  rows,
  issues,
  method,
  pathTemplate,
  onChange,
}: ToolInputsTableProps) => {
  const issueByIndex = new Map(issues.map(issue => [issue.index, issue.message]));
  // GET has no body, so every field rides in the query string and the location picker is locked.
  const locationLocked = inputLocationForMethod(method) === 'query';
  const pathPlaceholders = simplePathPlaceholderNames(pathTemplate);

  const update = (index: number, patch: Partial<ToolInputRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { name: '', type: 'string', required: false }]);

  return (
    <div className={styles.inputsTable}>
      {rows.length > 0 && (
        <>
          <dl className={styles.columnGuide}>
            <div>
              <dt>Name</dt>
              <dd>We send the value to your API under this exact name.</dd>
            </div>
            <div>
              <dt>Required</dt>
              <dd>
                Tick it when your API rejects the call without this value — the agent won&apos;t call the
                tool until it has it. Leave it off for everything else: the agent still asks once, then
                moves on if the contact skips it. Reach-filled and path-filling values are always on —
                the call can&apos;t be made without them.
              </dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>
                Where the value comes from. Leave it on <em>From the conversation</em> and the agent asks
                the contact for it. Pick a Reach value — like the contact&apos;s phone — and we fill it
                in automatically, so the agent never has to ask.
              </dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>
                How we send it to your API: in the request <em>Body</em>, or as a <em>Query</em>{' '}
                parameter on the URL. An input whose name matches a <code>{'{{placeholder}}'}</code> in
                the Path fills that placeholder instead.
              </dd>
            </div>
            <div>
              <dt>Description</dt>
              <dd>
                For values from the conversation: a plain-English note telling the agent what belongs
                here — always fill this in. For Reach-filled values the agent never sees this field, so a
                description is just a note for your own reference.
              </dd>
            </div>
          </dl>
          <table className={styles.paramTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Source</th>
                <th>Location</th>
                <th>Description</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <ToolInputRowEditor
                  key={index}
                  row={row}
                  issue={issueByIndex.get(index)}
                  locationLocked={locationLocked}
                  fillsPath={pathPlaceholders.has(row.name)}
                  onUpdate={patch => update(index, patch)}
                  onRemove={() => remove(index)}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
      <button type="button" onClick={add} className={styles.linkButton}>
        + Add parameter
      </button>
    </div>
  );
};
